import type { AnnotatedImageMeta, AnnotationCanvas } from "./canvas"
import type { AnnotationBadge, BoxGeometry } from "./geometry"
import {
	ANNOTATION_ACCENT,
	ANNOTATION_HALO,
	ANNOTATION_HALO_OPACITY,
	annotationHaloWidth,
	drawAnnotations,
} from "./canvas"

function escapeText(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
}

function escapeAttribute(value: string): string {
	return escapeText(value).replace(/"/g, "&quot;")
}

export class SvgAnnotationCanvas implements AnnotationCanvas {
	private readonly elements: string[] = []

	drawBox(geometry: BoxGeometry): void {
		const { x, y, width, height, radius, strokeWidth } = geometry
		const shape = `x="${x}" y="${y}" width="${width}" height="${height}" rx="${radius}" fill="none"`
		this.elements.push(
			`<rect ${shape} stroke="${ANNOTATION_HALO}" stroke-opacity="${ANNOTATION_HALO_OPACITY}" stroke-width="${annotationHaloWidth(strokeWidth)}"/>`,
			`<rect ${shape} stroke="${ANNOTATION_ACCENT}" stroke-width="${strokeWidth}"/>`,
		)
	}

	drawBadge(badge: AnnotationBadge, strokeWidth: number): void {
		this.elements.push(
			`<circle cx="${badge.cx}" cy="${badge.cy}" r="${badge.r}" fill="${ANNOTATION_ACCENT}" stroke="${ANNOTATION_HALO}" stroke-width="${strokeWidth * 0.8}"/>`,
			`<text x="${badge.cx}" y="${badge.cy}" fill="${ANNOTATION_HALO}" font-size="${badge.fontSize}" font-weight="600" font-family="Helvetica, Arial, sans-serif" text-anchor="middle" dominant-baseline="central">${escapeText(badge.text)}</text>`,
		)
	}

	toSvgContent(): string {
		return this.elements.join("")
	}
}

// The one drawing every surface shares: the chat overlay renders this string
// live, the server rasterizes the same string onto the PNG for email and
// live-chat HTML, so a mark looks identical wherever it shows up.
export function annotationSvg(
	image: AnnotatedImageMeta,
	opts?: { className?: string },
): string {
	const canvas = new SvgAnnotationCanvas()
	drawAnnotations(canvas, image)
	const classAttr = opts?.className
		? ` class="${escapeAttribute(opts.className)}"`
		: ""
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${image.width} ${image.height}" width="${image.width}" height="${image.height}"${classAttr} aria-hidden="true">${canvas.toSvgContent()}</svg>`
}
